import { outputReport, getOutputFormat, getPreOpenedWindow } from '@/utils/reportOutput';
import type { IEItem } from '@/services/iem';
import { getPersonLabels } from '@/hooks/usePersonLabels';
import {
  buildIeSummaryReportHtml,
  type IeSummaryMemberInfo,
  type IeSummaryItem,
  type IeSummaryCounts,
  type IeSummaryTemplateOptions,
} from '@/reports/templates/IeSummaryTemplate';

const convertIeItemToSummaryItem = (item: IEItem): IeSummaryItem => {
  return {
    kitName: item.kitName ?? null,
    lin: item.lin ?? null,
    nsn: item.nsn,
    nomenclature: item.item,
    qtyAuthorized: item.authorizedQty,
    qtyIssued: item.currentQty,
    status: item.status,
  };
};

const calculateIeCounts = (items: IEItem[]): IeSummaryCounts => {
  let compliant = 0;
  let shortage = 0;
  let overage = 0;
  let quantityOver = 0;
  let unauthorized = 0;

  items.forEach((item) => {
    if (item.currentQty === item.authorizedQty) {
      compliant++;
    } else if (item.currentQty < item.authorizedQty) {
      shortage++;
    } else {
      overage++;
      if (item.authorizedQty === 0) {
        unauthorized++;
      } else {
        quantityOver++;
      }
    }
  });

  return {
    total: items.length,
    compliant,
    shortage,
    overage,
    quantityOver,
    unauthorized,
  };
};

export const runIeSummaryReport = async (params: Record<string, unknown>): Promise<void> => {
  const ieItems = (params.ieItems as IEItem[] | undefined) ?? [];
  const member = params.member as IeSummaryMemberInfo;
  const ieKitLabel = (params.ieKitLabel as string | undefined) ?? 'IE Kit';
  const heading = (params.heading as string | undefined) ?? `${ieKitLabel} Summary`;
  const filtersSummary = params.filtersSummary as string | undefined;

  if (!member) {
    throw new Error(`Member information is required for ${ieKitLabel} Summary report`);
  }

  const items = ieItems.map(convertIeItemToSummaryItem);
  const counts = calculateIeCounts(ieItems);

  const templateOptions: IeSummaryTemplateOptions = {
    personLabels: getPersonLabels(params),
    generatedAt: new Date(),
    member,
    items,
    counts,
    heading,
    filtersSummary,
    ieKitLabel,
  };

  const html = buildIeSummaryReportHtml(templateOptions);

  const safeName = member.name.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '');
  const filename = `ie-summary-${safeName || 'member'}.html`;

  outputReport(html, filename, getOutputFormat(params), getPreOpenedWindow(params));
};
